// 角色权限矩阵（前端体验层镜像）—— 与 apps/core-api 的 PERMISSION_MATRIX 严格对齐（最小权限）
// 注意：这里只用于按钮/入口的体验层显隐，真正的强制在后端（requireReadPermission / requireWritePermission
// / STATUS_FLOWS / canPrint）。前端隐藏不代表安全边界。后端矩阵快照可通过 GET /api/permissions 读取。

import type { Role, User } from './types';

// —— 统一身份角色（方案 1）——
// Integral 登录后 user.role 可能是统一身份角色（company_admin/engineer/finance/customer），
// 而不是旧 users 表的岗位角色。权限矩阵按岗位角色编写，故先经此映射展开，
// 再用既有矩阵判定 —— 与后端 apps/core-api/src/identity/integral-role-map.js 严格同源。
//
// 口径与后端保持一致：
//   engineer      = sales + production + quality + warehouse（完整工程业务，不含 admin/finance）
//   company_admin = admin
//   finance       = sales + production + quality + warehouse + finance
//   customer      = []（不进入 Integral 业务控制台）
const IDENTITY_ROLE_MAP: Record<string, Role[]> = {
  engineer: ['sales', 'production', 'quality', 'warehouse'],
  company_admin: ['admin'],
  finance: ['sales', 'production', 'quality', 'warehouse', 'finance'],
  customer: [],
};

// 业务操作员口径（与 core-api 的 BIZ_ROLES 严格同源）：受控清理入口
// （删库存流水 / 删维修版本）按「管理员 + BIZ_ROLES」放开，与后端路由判定一致。
export const BIZ_ROLES: Role[] = ['sales', 'production', 'quality', 'warehouse'];

// 取消/作废放权角色集（2026-10-16，与 core-api 的 CANCEL_ROLES 同源）：
// 业务操作员 + 管理员。后端 transitionStatus 无 admin 短路，快照里必须显式含 admin。
export const CANCEL_ROLES: Role[] = [...BIZ_ROLES, 'admin'];

/** 该用户的全部岗位角色（统一身份角色会被展开；旧账号角色原样保留）。 */
export function rolesOf(user: User | null | undefined): Role[] {
  const role = user?.role as string | undefined;
  if (!role) return [];
  const mapped = IDENTITY_ROLE_MAP[role];
  if (mapped) return [...mapped];
  return [role as Role];
}

/**
 * [PATCH-6] 读取白名单：与后端 PERMISSION_MATRIX 的 view 逐模块一致。
 * 之所以显式登记，是为让「按实际角色判定」这一层可读可核；
 * 数值必须与后端 app.js 的 PERMISSION_MATRIX 同源，登记项均可回溯。
 */
const VIEW_WHITELIST: Partial<Record<PermissionModule, Role[]>> = {
  // 与后端逐模块一致（admin 短路）
  'material-plans': ['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'],
  'price-lists': ['admin', 'sales', 'finance', 'production', 'quality', 'warehouse', 'viewer'],
  // [PATCH-6 修正] 以下两项此前照抄前端矩阵，漏掉了后端已放开的 sales/quality/production，
  // 会让这些人看到「入口在、点下去 404/403」。此处以**后端**为准补齐。
  'suppliers': ['admin', 'warehouse', 'production', 'quality', 'sales', 'finance', 'viewer'],
  'purchase-orders': ['admin', 'warehouse', 'production', 'quality', 'sales', 'finance', 'viewer'],
  'arca-invoices': ['admin', 'finance'],
  'payments': ['admin', 'finance'],
};

/**
 * [PATCH-6 修正] 读取可见性：与后端 canViewModule(user.role, module) 逐模块一致。
 *
 * 根因（不是「finance 一律不可见」）：
 *   后端 identity/integral-session.js:56 对身份用户构造 user.role 的规则是
 *     role = identityRole === 'finance' ? 'finance' : internalRoles[0]
 *   即 **finance 特判为单一 'finance' 角色**，并不展开成 sales/production/...；
 *   而 engineer 取 internalRoles[0]（= 'sales'）作为单一角色。
 *   前端 IDENTITY_ROLE_MAP 过去把 finance 展开成 5 个角色，于是
 *   material-plans（view 白名单不含 finance）在前端被判可见，后端 403。
 *
 * 因此这里按后端同一规则求【实际参与判定的那个角色】，而不是全局短路 finance。
 * 这样 finance 仍可正常访问 price-lists / purchase-orders / arca-invoices / payments
 * 等后端确实允许 finance 的模块，不会被误隐藏。
 */
function effectiveRole(user: User | null | undefined): Role | null {
  const raw = user?.role as string | undefined;
  if (!raw) return null;
  // 旧账号：角色原样参与判定
  if (!IDENTITY_ROLE_MAP[raw]) return raw as Role;
  // 统一身份 finance：后端特判为 'finance'
  if (raw === 'finance') return 'finance';
  // 其余统一身份：取 internalRoles[0]
  return IDENTITY_ROLE_MAP[raw][0] as Role;
}

export function canView(user: User | null | undefined, module: PermissionModule): boolean {
  const r = effectiveRole(user);
  if (!r) return false;
  if (r === 'admin') return true;
  return (VIEW_WHITELIST[module] ?? PERMISSION_MATRIX[module]?.view ?? []).includes(r);
}

/**
 * 该用户是否具备某个岗位角色。
 * [PATCH-6] 与后端单角色判定对齐：统一身份用户按其**实际参与判定的角色**比较，
 * 而非展开集合——否则 engineer（实际角色 'sales'）会被误判为具备 'warehouse' 等。
 */
export function hasRole(user: User | null | undefined, role: Role): boolean {
  return effectiveRole(user) === role;
}

export type PermissionModule =
  | 'discount-rules'
  | 'customers' | 'suppliers' | 'items' | 'item-groups' | 'item-code-rules' | 'operations' | 'warehouses' | 'boms'
  | 'process-templates' | 'material-plans' | 'qc-templates' | 'qc-review'
  | 'stock/movements' | 'work-orders' | 'price-lists' | 'purchase-orders'
  | 'contracts' | 'quotations' | 'deliveries' | 'inspections' | 'returns' | 'customer-supplied-materials' | 'physical-assets' | 'arca-invoices' | 'payments' | 'attachments' | 'leads'
  | 'rework-batches'
  | 'production-rework'
  | 'users';

export type PermissionOp = 'view' | 'create' | 'edit' | 'transition' | 'cancel' | 'print';

interface ModulePermission {
  view: Role[];
  create: Role[];
  edit: Role[];
  transition: Role[];
  cancel: Role[];
  print: Role[];
}

const m = (
  view: Role[],
  create: Role[],
  edit: Role[] = create,
  transition: Role[] = [],
  print: Role[] = [],
  // cancel 默认仅管理员；报价/合同/交货/工单的「取消」自 2026-10-16 起下放 BIZ_ROLES
  // （用户决定：员工可直接取消，原因必填 + 审计 + 后端业务校验不变）。
  cancel: Role[] = ['admin'],
): ModulePermission => ({ view, create, edit, transition, cancel, print });

// 与后端逐模块 view/create/edit/transition/cancel/print 一一对应。
export const PERMISSION_MATRIX: Record<PermissionModule, ModulePermission> = {
  'discount-rules': m(['admin','sales','production','quality','warehouse','finance','viewer'], BIZ_ROLES, BIZ_ROLES),
  customers: m(['admin', 'sales', 'warehouse', 'production', 'finance', 'viewer'], ['sales'], ['admin']),
  items: m(['admin', 'sales', 'production', 'quality', 'warehouse', 'finance', 'viewer'], ['sales', 'production', 'warehouse'], ['admin']),
  'item-groups': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'finance', 'viewer'], ['admin'], ['admin']),
  // [PATCH-6 修正] 与后端 app.js:978 对齐：
//   后端 create/edit 均为 BIZ_ROLES，前端原为 create=['warehouse'] / edit=['admin']，
//   会让 sales/production/quality 看到入口却拿不到写权限（反之亦然）。
suppliers: m(['admin', 'warehouse', 'production', 'quality', 'sales', 'finance', 'viewer'], BIZ_ROLES, BIZ_ROLES),
  // [PATCH-6 修正] 与后端 app.js:979 对齐（view 同上，create/edit 为 BIZ_ROLES）
'purchase-orders': m(['admin', 'warehouse', 'production', 'quality', 'sales', 'finance', 'viewer'], BIZ_ROLES, BIZ_ROLES),
  // [PATCH-6 修正] 与后端 app.js:980 对齐：
//   view 缺 production/quality/warehouse；create/edit 后端为 BIZ_ROLES。
'price-lists': m(['admin', 'sales', 'finance', 'production', 'quality', 'warehouse', 'viewer'], BIZ_ROLES, BIZ_ROLES),
  boms: m(['admin', 'sales', 'production', 'warehouse', 'finance', 'viewer'], ['production'], ['admin'], ['admin', 'sales', 'production', 'quality', 'warehouse']),
  // 工艺方案三层（P6-5）：生产建单/编辑/审批流转，质量·仓储·销售·只读可查看，编辑落管理员，与后端对齐。
  'process-templates': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'], ['production'], ['production'], ['production']),
  // [PATCH-5] 与后端 PERMISSION_MATRIX['material-plans'] 对齐：
  //   后端 create/transition 均为 BIZ_ROLES（sales/production/quality/warehouse），
  //   原前端只给 production，导致「接口允许、按钮隐藏」——业务角色调得通却看不到入口。
  'material-plans': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'], BIZ_ROLES, BIZ_ROLES),
  // 质检模板 / 量化公差主数据（接管）：质量创建/编辑/复核批准，生产·仓库·销售·查看只读，财务不涉及。与后端 PERMISSION_MATRIX 对齐。
  'qc-templates': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'], ['quality'], ['quality']),
  // 决策二：工序「是否需检」的三态审核工作台。质量可判定（create=edit=quality），
  // 生产/仓库/销售/查看只读——让他们看到「还差几道没审」有助于推进，但不给他们判定权。与后端 PERMISSION_MATRIX['qc-review'] 对齐。
  'qc-review': m(['admin', 'production', 'quality', 'warehouse', 'viewer'], ['quality'], ['quality']),
  'work-orders': m(['admin', 'sales', 'production', 'warehouse', 'finance', 'viewer'], ['production'], ['production'], [], [], CANCEL_ROLES),
  operations: m(['admin', 'sales', 'production', 'warehouse', 'finance', 'viewer'], ['production'], ['admin']),
  'item-code-rules': m(['admin', 'production'], ['production'], ['admin']),
  warehouses: m(['admin', 'sales', 'production', 'quality', 'warehouse', 'finance', 'viewer'], ['warehouse'], ['admin']),
  'stock/movements': m(['admin', 'production', 'warehouse', 'viewer'], ['warehouse', 'production', 'sales', 'finance'], []),
  quotations: m(['admin', 'sales', 'finance', 'viewer'], ['sales'], ['sales'], ['sales'], ['admin', 'sales', 'finance'], CANCEL_ROLES),
  contracts: m(['admin', 'sales', 'warehouse', 'production', 'quality', 'finance', 'viewer'], ['sales'], ['sales'], ['sales'], ['admin', 'sales', 'finance'], CANCEL_ROLES),
  deliveries: m(['admin', 'sales', 'production', 'quality', 'warehouse', 'finance', 'viewer'], ['sales', 'warehouse'], ['sales', 'warehouse'], ['sales', 'warehouse'], ['admin', 'sales', 'warehouse'], CANCEL_ROLES),
  inspections: m(['admin', 'sales', 'production', 'quality', 'warehouse', 'finance', 'viewer'], ['quality'], ['admin'], [], ['admin', 'quality', 'sales', 'warehouse']),
  returns: m(['admin', 'sales', 'warehouse', 'viewer'], ['sales', 'warehouse'], ['admin']),
  // 售后返工作业批次（客户售后返工）：与后端 PERMISSION_MATRIX['rework-batches'] 同源。
  'rework-batches': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'], BIZ_ROLES, BIZ_ROLES),
  // 厂内过程返工任务：与后端 PERMISSION_MATRIX['production-rework'] 同源（view 不含 finance）。
  'production-rework': m(['admin', 'sales', 'warehouse', 'production', 'quality', 'viewer'], BIZ_ROLES, BIZ_ROLES),
  // 客户来料（维修/加工）：查看含质检；建单/交还/状态流转为 仓储·生产·销售；编辑仅管理员。
  'customer-supplied-materials': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'], ['warehouse', 'production', 'sales'], ['admin'], ['warehouse', 'production', 'sales']),
  // 工件主档 + 维修版本（阶段6）：查看含质检/只读；建档/开版本/状态流转为 生产·质检·仓储；编辑仅管理员。与后端 PERMISSION_MATRIX 对齐。
  'physical-assets': m(['admin', 'sales', 'production', 'quality', 'warehouse', 'viewer'], ['production', 'quality', 'warehouse'], ['admin'], ['production', 'quality', 'warehouse']),
  leads: m(['admin', 'sales', 'viewer'], ['sales'], ['sales']),
  'arca-invoices': m(['admin', 'finance'], ['finance'], ['admin']),
  payments: m(['admin', 'finance'], ['finance'], ['admin']),
  // 附件基础模型：业务操作员 + 财务可上传与查看；编辑/替换仅管理员。
  // 与后端 PERMISSION_MATRIX.attachments 对齐；不含 viewer（附件含客户与税务原件）。
  attachments: m(['admin', 'finance', 'sales', 'production', 'quality', 'warehouse'], ['finance', 'sales', 'production', 'quality', 'warehouse'], ['admin']),
  users: m(['admin'], ['admin'], ['admin']),
};

// 统一权限判定：admin 短路放行（与后端 requireReadPermission/requireWritePermission 一致）。
//
// [PATCH-6 修正] 关键：后端判定用的是**单一** user.role
// （身份登录时 role = finance ? 'finance' : internalRoles[0]，见 integral-session.js:56），
// 而不是展开后的角色集合。因此前端也必须按同一个实际角色判定，
// 否则「前端按展开角色放行、后端按单角色拒绝」会产生按钮可见但请求 403。
//   identity engineer → 实际角色 'sales'      （不是 sales/production/quality/warehouse）
//   identity finance  → 实际角色 'finance'
//   identity company_admin → 实际角色 'admin'
export function can(user: User | null | undefined, module: PermissionModule, op: PermissionOp): boolean {
  if(module==='discount-rules') {
    const raw=String(user?.role||'');const role=raw==='finance'?'finance':rolesOf(user)[0];
    if(role==='admin')return true;
    return op==='view'?['sales','production','quality','warehouse','finance','viewer'].includes(role): (op==='create'||op==='edit')&&BIZ_ROLES.includes(role);
  }
  const r = effectiveRole(user);
  if (!r) return false;
  if (r === 'admin') return true;
  if (op === 'view') return canView(user, module);
  const allowed = PERMISSION_MATRIX[module]?.[op] ?? [];
  return allowed.includes(r);
}

// 草稿可编辑 = 角色具备 edit 权限 且（管理员或本人创建）。后端会二次校验，前端仅作体验层。
export function canEditOwnDraft(
  user: User | null | undefined,
  module: PermissionModule,
  record: { created_by?: string | null },
): boolean {
  if (!user) return false;
  if (!can(user, module, 'edit')) return false;
  if (hasRole(user, 'admin')) return true;
  return !!record.created_by && record.created_by === user.id;
}

// 审计读取（与后端路由内联强制保持一致）。全量审计：管理员 + 财务（财务由后端强制收窄为财务相关）。
export function canViewFullAudit(user: User | null | undefined): boolean {
  const r = effectiveRole(user);
  return r === 'admin' || r === 'finance';
}
export function canViewRecordAudit(user: User | null | undefined): boolean {
  const r = effectiveRole(user);
  if (!r) return false;
  return r === 'admin' || (['sales', 'warehouse', 'finance'] as Role[]).includes(r);
}

// 采购成本字段级权限：仅 admin/finance 可见可改。与 core-api canManagePurchaseCost 同源。
export function canManagePurchaseCost(user: User | null | undefined): boolean {
  const r = effectiveRole(user);
  return r === 'admin' || r === 'finance';
}
